import React from 'react';
import { 
  Search, Plus, Calendar, Mail, Clock, Briefcase, 
  ChevronRight, AlertCircle, FileText
} from 'lucide-react';

/**
 * HeroMock - A beautiful, static preview of the Lexy dashboard
 * Built with real components for crisp rendering
 * Designed to showcase the product in a trust-building way
 * 
 * Linear-inspired: minimal, clean, purposeful
 */
const HeroMock = () => {
  // Static demo data - realistic law firm scenario
  const stats = {
    cases: 12,
    clients: 28,
    tasks: 5,
    inbox: 3,
    unbilled: 47500
  };

  const mockCases = [
    { id: '1', title: 'Eiendomsoverdragelse Olsen', client: 'Olsen AS', status: 'active', dueDate: 'I dag' },
    { id: '2', title: 'Arbeidskontrakt Berg', client: 'Berg Consulting', status: 'active', dueDate: 'I morgen' },
    { id: '3', title: 'Selskapsavtale Nordvik', client: 'Nordvik Holding', status: 'review', dueDate: '24. jan' },
  ];

  const activityData = [3, 5, 2, 7, 4, 6, 8];
  const maxActivity = Math.max(...activityData);
  const days = ['MA', 'TI', 'ON', 'TO', 'FR', 'LØ', 'SØ'];

  return (
    <div className="bg-[#FAFAFA] rounded-2xl overflow-hidden shadow-2xl shadow-gray-200/60 border border-gray-200/80">
      {/* Window chrome - minimal */}
      <div className="flex items-center gap-2 px-4 py-3 bg-white border-b border-gray-100">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-gray-200" />
          <div className="w-3 h-3 rounded-full bg-gray-200" />
          <div className="w-3 h-3 rounded-full bg-gray-200" />
        </div>
        <div className="flex-1 flex justify-center">
          <div className="px-4 py-1 bg-gray-50 rounded-md text-xs text-gray-400 font-medium">
            lexy.no/dashboard
          </div>
        </div>
        <div className="w-12" />
      </div>

      {/* Dashboard content */}
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gray-900 rounded-full flex items-center justify-center text-white text-sm font-medium">
              EN
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900 tracking-tight">
                God morgen, Erik
              </h2>
              <p className="text-xs text-gray-500">
                Onsdag 22. januar
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Search bar */}
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" strokeWidth={1.5} />
              <div className="w-40 h-8 bg-white border border-gray-200 rounded-lg pl-8 pr-2 flex items-center">
                <span className="text-xs text-gray-400">Søk...</span>
                <span className="ml-auto text-[9px] text-gray-400 bg-gray-100 px-1 py-0.5 rounded">⌘K</span>
              </div>
            </div>
            <div className="w-7 h-7 bg-gray-900 rounded-lg flex items-center justify-center">
              <Plus className="w-3.5 h-3.5 text-white" strokeWidth={2} />
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div className="bg-gray-50/80 rounded-xl px-5 py-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-semibold text-gray-900 tabular-nums">{stats.cases}</div>
              <div className="text-[9px] font-medium text-gray-400 uppercase tracking-wider mt-0.5">AKTIVE SAKER</div>
            </div>
            <div className="w-px h-8 bg-gray-200" />
            <div>
              <div className="text-2xl font-semibold text-gray-900 tabular-nums">{stats.clients}</div>
              <div className="text-[9px] font-medium text-gray-400 uppercase tracking-wider mt-0.5">KLIENTER</div>
            </div>
            <div className="w-px h-8 bg-gray-200" />
            <div>
              <div className="text-2xl font-semibold text-gray-900 tabular-nums">{stats.tasks}</div>
              <div className="text-[9px] font-medium text-gray-400 uppercase tracking-wider mt-0.5">OPPGAVER</div>
            </div>
            <div className="w-px h-8 bg-gray-200" />
            <div>
              <div className="text-2xl font-semibold text-gray-900 tabular-nums">{stats.inbox}</div>
              <div className="text-[9px] font-medium text-gray-400 uppercase tracking-wider mt-0.5">INNBOKS</div>
            </div>
            <div className="w-px h-8 bg-gray-200" />
            <div>
              <div className="text-2xl font-semibold text-gray-900 tabular-nums">{stats.unbilled.toLocaleString('nb-NO')}<span className="text-sm text-gray-400 ml-0.5 font-normal">kr</span></div>
              <div className="text-[9px] font-medium text-gray-400 uppercase tracking-wider mt-0.5">UFAKTURERT</div>
            </div>
          </div>
        </div>

        {/* Two column layout */}
        <div className="grid grid-cols-2 gap-6">
          {/* Left - Activity Chart */}
          <div>
            <div className="flex items-end justify-between gap-2 h-16 mb-2">
              {activityData.map((value, idx) => {
                const isToday = idx === activityData.length - 1;
                return (
                  <div key={idx} className="flex-1 flex flex-col items-center justify-end h-full">
                    <div 
                      className={`w-3 rounded-sm ${isToday ? 'bg-gray-900' : 'bg-gray-200'}`}
                      style={{ height: `${(value / maxActivity) * 100}%`, minHeight: '6px' }}
                    />
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between gap-2">
              {days.map((day, idx) => (
                <div key={idx} className="flex-1 text-center">
                  <span className={`text-[8px] tracking-wide ${idx === days.length - 1 ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
                    {day}
                  </span>
                </div>
              ))}
            </div>
            
            {/* Quick stats under chart */}
            <div className="mt-5 grid grid-cols-3 gap-4">
              <div>
                <div className="flex items-center gap-1 mb-1">
                  <Calendar className="w-3 h-3 text-gray-400" strokeWidth={1.5} />
                  <span className="text-[8px] font-medium text-gray-400 uppercase tracking-wide">NESTE FRIST</span>
                </div>
                <p className="text-xs font-medium text-gray-900 truncate">Eiendomsoverdragelse</p>
                <p className="text-[10px] text-gray-500">I dag</p>
              </div>
              <div className="text-center">
                <div className="flex items-center gap-1 mb-1 justify-center">
                  <Mail className="w-3 h-3 text-gray-400" strokeWidth={1.5} />
                  <span className="text-[8px] font-medium text-gray-400 uppercase tracking-wide">INNBOKS</span>
                </div>
                <div className="flex items-baseline gap-0.5 justify-center">
                  <span className="text-lg font-semibold text-gray-900 tabular-nums">3</span>
                  <span className="text-[10px] text-gray-500">uleste</span>
                </div>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-1 mb-1 justify-end">
                  <Clock className="w-3 h-3 text-gray-400" strokeWidth={1.5} />
                  <span className="text-[8px] font-medium text-gray-400 uppercase tracking-wide">I DAG</span>
                </div>
                <div className="flex items-baseline gap-0.5 justify-end">
                  <span className="text-lg font-semibold text-gray-900 tabular-nums">4.5</span>
                  <span className="text-[10px] text-gray-500">t</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Cases List */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-medium text-gray-900">Mine saker</h3>
              <span className="text-[9px] text-gray-400 font-medium">Se alle →</span>
            </div>
            <div className="space-y-2">
              {mockCases.map((caseItem) => (
                <div 
                  key={caseItem.id}
                  className="flex items-center gap-3 p-2.5 bg-white rounded-lg border border-gray-100"
                >
                  <div className="w-7 h-7 bg-gray-100 rounded-md flex items-center justify-center">
                    <Briefcase className="w-3.5 h-3.5 text-gray-500" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-900 truncate">{caseItem.title}</p>
                    <p className="text-[10px] text-gray-500">{caseItem.client}</p>
                  </div>
                  <div className="text-right">
                    <span className={`text-[10px] font-medium ${
                      caseItem.dueDate === 'I dag' ? 'text-amber-600' : 'text-gray-400'
                    }`}>
                      {caseItem.dueDate}
                    </span>
                  </div>
                  <ChevronRight className="w-3.5 h-3.5 text-gray-300" strokeWidth={1.5} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroMock;
